import type { DictionaryValidationFeedsStatus } from "@shared-core/data/cost-engine-validation-types";
import { formatRollingValidationSetSummary } from "@shared-core/domain/cost-engine-validation-presentation";
import { formatDateTimeOrDash } from "@shared-core/domain/formatters";
import { Wifi, WifiOff } from "lucide-react";

/**
 * Feed health for the two check sources, "Checked at estimate" and "Rolling
 * validation" (design brief §8, journey 1; review decisions in
 * `02_glossary_and_live_costing.md`) — the latter merged into one card
 * naming the set size, the weekly budget used, and last/next run, and never
 * hiding an over-budget set. Lives on the Live costing › Checks tab only
 * (decision 12 Sep — the dictionary index lost it, and this card no longer
 * links out to Checks because it now renders exclusively there).
 *
 * "Checked at estimate" (product-owner decision, 12 Sep): both checkouts —
 * Send estimate and Order now — make exactly one Tharstern EstRequest
 * submission; Order now is that same call followed by setting the estimate
 * to ORDERED. So the check happens at estimate time regardless of which
 * checkout button raised it, and the feed is named for what actually
 * happens, not for which button was clicked.
 */
export function FeedsBanner({
  status,
}: {
  status: DictionaryValidationFeedsStatus;
}) {
  return (
    <div className="grid gap-3 rounded-lg border border-border bg-muted/30 px-4 py-3 text-sm sm:grid-cols-2">
      <FeedSummary
        label="Checked at estimate"
        healthy={status.atEstimate.healthy}
        primary={`${status.atEstimate.countToday ?? 0} today`}
        secondary={`${status.atEstimate.failuresToday ?? 0} failed · last ${formatDateTimeOrDash(status.atEstimate.lastRunAt)}`}
      />
      <div className="flex flex-col gap-0.5">
        <span className="flex items-center gap-1.5 font-medium">
          <Wifi
            className={`size-3.5 ${status.rollingValidation.healthy ? "text-[color:var(--color-success)]" : "text-destructive"}`}
          />
          Rolling validation
        </span>
        <span className="text-muted-foreground">
          {formatRollingValidationSetSummary(status)}
        </span>
        <span className="text-xs text-muted-foreground">
          {status.rollingValidation.mintsUsedThisWeek ?? 0} of{" "}
          {status.rollingValidation.mintBudgetThisWeek ?? 0} checks used this
          week · last {formatDateTimeOrDash(status.rollingValidation.lastRunAt)}{" "}
          · next{" "}
          {formatDateTimeOrDash(status.rollingValidation.nextScheduledAt)}
        </span>
        {/* Scoped runs (`cis cost-check run --item …`) spend the same weekly
            allowance as the scheduled run, so the figure above counts them —
            naming them here is what makes the figure explainable rather than
            merely higher than expected. */}
        {/* Quarantined prices (spec 03 §5.1 step 4c) are not in the set
            size above: no run checks them until someone releases them on
            their item's page, so they wait on a person, not on the budget. */}
        {status.quarantinedPriceCount > 0 && (
          <span className="text-xs font-medium text-destructive">
            {status.quarantinedPriceCount}{" "}
            {status.quarantinedPriceCount === 1 ? "price" : "prices"}{" "}
            quarantined after a Tharstern timeout — release on the item
          </span>
        )}
        {(status.rollingValidation.scopedRunsThisWeek ?? []).length > 0 && (
          <span className="text-xs text-muted-foreground">
            Including{" "}
            {(status.rollingValidation.scopedRunsThisWeek ?? [])
              .map(
                (run) =>
                  `${run.scope} (${run.mintsAttempted} mint${run.mintsAttempted === 1 ? "" : "s"})`,
              )
              .join(", ")}
          </span>
        )}
      </div>
    </div>
  );
}

function FeedSummary({
  label,
  healthy,
  primary,
  secondary,
}: {
  label: string;
  healthy: boolean;
  primary: string;
  secondary: string;
}) {
  const Icon = healthy ? Wifi : WifiOff;
  return (
    <div className="flex flex-col gap-0.5">
      <span className="flex items-center gap-1.5 font-medium">
        <Icon
          className={`size-3.5 ${healthy ? "text-[color:var(--color-success)]" : "text-destructive"}`}
        />
        {label}
      </span>
      <span className="text-muted-foreground">{primary}</span>
      <span className="text-xs text-muted-foreground">{secondary}</span>
    </div>
  );
}

/** Couldn't load feed status — the caller's fallback banner, shared so both
 *  pages show the same wording. */
export function FeedsBannerError() {
  return (
    <p className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
      Couldn&rsquo;t load feed status — the checked-at-estimate and rolling
      validation feeds may be unreachable. Cost-check figures below may be
      stale.
    </p>
  );
}
